import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import {
  selectHasSupportedAssociationCapability,
  selectPersonaKey,
  selectTokenUserId,
} from "../store/auth-slice";
import { fetchMyAssociations } from "../store/associationContext-actions";

// Association portal: headless. Mounted next to the other global managers so
// the administered associations are loaded on fresh login and on session
// restore, independently of which route the user lands on. Port of
// AdvisorContextLoader minus the 404 branch (`/mine` has none: an empty list
// is a loaded state).
const AssociationContextLoader = () => {
  const dispatch = useDispatch();
  const hasCapability = useSelector(selectHasSupportedAssociationCapability);
  const userId = useSelector(selectTokenUserId);
  const personaKey = useSelector(selectPersonaKey);
  const status = useSelector(
    (state: RootState) => state.associationContext.status,
  );

  const armed = hasCapability && userId != null;

  // (a) initial load: fresh login, session restore, AND the persona-changing
  // token refresh. `personaKey` is a dependency because `auth/personaExit`
  // resets the context slice while `armed` and `userId` may survive.
  useEffect(() => {
    if (!armed || userId == null) {
      return;
    }
    const controller = new AbortController();
    dispatch<any>(fetchMyAssociations(userId, controller.signal));
    return () => controller.abort();
  }, [dispatch, armed, userId, personaKey]);

  // (b) window focus retries a FIRST load that failed outright. `error` is
  // only reachable with nothing loaded, and no other automatic trigger can
  // retry it.
  const loadFailed = armed && userId != null && status === "error";
  useEffect(() => {
    if (!loadFailed) {
      return;
    }
    const onFocus = () => {
      if (userId != null) {
        dispatch<any>(fetchMyAssociations(userId));
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [dispatch, loadFailed, userId]);

  return null;
};

export default AssociationContextLoader;
